<template>
  <!-- Altura = viewport menos folga inferior; scroll só dentro do painel de mensagens -->
  <div
    class="pt-20 pb-3 box-border flex flex-col overflow-hidden h-[calc(100vh-12px)] max-h-[calc(100vh-12px)]"
  >
    <div class="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pt-2 sm:pt-4 flex flex-col flex-1 min-h-0">
      <header class="shrink-0 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-2 sm:gap-4 mb-2 sm:mb-4">
        <div class="min-w-0">
          <h1 class="text-xl font-bold text-slate-100 tracking-tight sm:text-2xl lg:text-3xl">
            Chat com coordenadores
          </h1>
          <p class="mt-0.5 text-xs text-slate-400 sm:hidden">Converse com os coordenadores de IA</p>
          <p class="mt-0.5 hidden max-w-3xl text-sm text-slate-400 sm:mt-1 sm:block">
            Converse com os coordenadores de IA sobre: dúvidas, informações e execução de tarefas.
          </p>
        </div>
        <div
          class="flex w-full min-w-0 flex-nowrap items-center gap-1.5 sm:w-auto sm:gap-2 lg:shrink-0"
        >
          <button
            type="button"
            class="shrink-0 rounded-lg border border-slate-500/60 px-2 py-1.5 text-[11px] font-medium text-slate-300 transition hover:border-cyan-400/30 hover:bg-slate-800/80 sm:px-3 sm:py-2 sm:text-xs"
            @click="startNewConversation"
          >
            Nova conversa
          </button>
          <button
            type="button"
            class="shrink-0 rounded-lg border border-slate-500/60 px-2 py-1.5 text-[11px] font-medium text-slate-300 transition hover:border-cyan-400/30 hover:bg-slate-800/80 sm:px-3 sm:py-2 sm:text-xs"
            @click="openHistory"
          >
            Histórico
          </button>
          <span
            class="min-w-0 flex-1 truncate rounded border border-slate-700/60 bg-slate-950/50 px-1.5 py-1 text-left font-mono text-[10px] leading-tight text-slate-400 sm:max-w-md sm:flex-none sm:px-2 sm:text-[11px]"
            :title="`Sessão: ${conversationId}`"
          >
            Sessão: {{ conversationId }}
          </span>
        </div>
      </header>

      <!-- Modal histórico -->
      <div
        v-if="historyOpen"
        class="fixed inset-0 z-50 flex items-center justify-center px-4"
        @keydown.esc="closeHistory"
      >
        <div class="absolute inset-0 bg-slate-950/35 backdrop-blur-[2px]" @click="closeHistory" />
        <div
          class="relative w-full max-w-2xl h-[min(85vh,36rem)] max-h-[90vh] rounded-2xl border border-slate-500 bg-slate-700 shadow-2xl shadow-black/35 flex flex-col overflow-hidden"
        >
          <div class="px-5 py-4 border-b border-slate-500 flex items-center justify-between gap-3 shrink-0">
            <div>
              <h3 class="text-sm font-semibold text-slate-100">Histórico de conversas</h3>
              <p class="text-[11px] text-slate-300 mt-0.5">
                {{ historyPerPage }} por página
                <template v-if="historyMeta.total_count != null && !historyLoading">
                  <template v-if="historyMeta.total_count > 0">
                    · Página {{ historyMeta.page }} de {{ historyMeta.total_pages }}
                    ({{ historyMeta.total_count }}
                    {{ historyMeta.total_count === 1 ? 'conversa' : 'conversas' }})
                  </template>
                  <template v-else>· 0 conversas</template>
                </template>
              </p>
            </div>
            <button
              type="button"
              class="text-xs px-3 py-2 rounded-lg border border-slate-400 text-slate-100 hover:bg-slate-600 transition"
              @click="closeHistory"
            >
              Fechar
            </button>
          </div>

          <div class="flex-1 flex flex-col min-h-0 p-5 gap-4 overflow-hidden">
            <div class="grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end shrink-0">
              <div>
                <label class="block text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">
                  Nº da sessão
                </label>
                <input
                  v-model.trim="historySessionQuery"
                  type="text"
                  autocomplete="off"
                  placeholder="Contém…"
                  class="w-full text-xs px-3 py-2 rounded-lg border border-slate-500 bg-slate-800 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 font-mono"
                  @keydown.enter.prevent="applyHistoryFilters"
                />
              </div>
              <div>
                <label class="block text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">
                  Data
                </label>
                <input
                  v-model="historyDateQuery"
                  type="date"
                  class="history-modal-date w-full text-xs px-3 py-2 rounded-lg border border-slate-500 bg-slate-800 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
                />
              </div>
              <div class="flex flex-wrap gap-2 sm:justify-end">
                <button
                  type="button"
                  class="text-xs px-3 py-2 rounded-lg border border-slate-400 text-slate-100 hover:bg-slate-600 transition"
                  @click="applyHistoryFilters"
                >
                  Filtrar
                </button>
                <button
                  v-if="historyHasActiveFilters"
                  type="button"
                  class="text-xs px-3 py-2 rounded-lg border border-slate-500 text-slate-300 hover:bg-slate-600/80 transition"
                  @click="clearHistoryFilters"
                >
                  Limpar
                </button>
              </div>
            </div>

            <div
              class="flex-1 min-h-0 rounded-xl border border-slate-600/40 bg-slate-800/25 flex flex-col overflow-hidden"
            >
              <div v-if="historyLoading" class="flex-1 flex items-center justify-center text-sm text-slate-200 px-4">
                Carregando…
              </div>
              <div
                v-else-if="historyError"
                class="flex-1 flex items-center justify-center p-4"
              >
                <p class="text-sm text-red-300 bg-red-500/10 border border-red-400/30 rounded-xl p-3 w-full text-center">
                  {{ historyError }}
                </p>
              </div>
              <div
                v-else-if="!historyItems.length"
                class="flex-1 flex items-center justify-center text-sm text-slate-200 px-4 text-center"
              >
                Nenhuma conversa encontrada.
              </div>
              <div
                v-else
                :class="['flex-1 min-h-0 overflow-y-auto px-2 py-2 space-y-2 pr-1', modalScrollbarClass]"
              >
                <div
                  v-for="item in historyItems"
                  :key="item.conversation_id"
                  class="rounded-xl border border-slate-500 bg-slate-800 px-4 py-3 flex items-start justify-between gap-4"
                >
                  <div class="min-w-0">
                    <p class="text-sm text-slate-100 leading-snug break-words">
                      {{ item.first_message_preview || '(Sem mensagem)' }}
                    </p>
                    <div class="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                      <p class="text-[11px] text-slate-300 font-mono truncate max-w-[260px]" :title="item.conversation_id">
                        {{ item.conversation_id }}
                      </p>
                      <p v-if="item.last_run_at" class="text-[11px] text-slate-300">
                        {{ formatDateTime(item.last_run_at) }}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    class="shrink-0 text-xs px-3 py-2 rounded-lg bg-cyan-500 border border-cyan-300 text-slate-100 hover:bg-cyan-400 hover:text-slate-50 transition disabled:opacity-60"
                    :disabled="historyLoadingConversationId === item.conversation_id"
                    @click="loadConversation(item.conversation_id)"
                  >
                    {{ historyLoadingConversationId === item.conversation_id ? 'Carregando…' : 'Carregar' }}
                  </button>
                </div>
              </div>
            </div>

            <div class="min-h-[44px] shrink-0 flex flex-wrap items-center justify-between gap-2 pt-1 border-t border-slate-600/80">
              <template v-if="!historyLoading && !historyError && historyMeta.total_pages > 1">
                <button
                  type="button"
                  class="text-xs px-3 py-2 rounded-lg border border-slate-500 text-slate-200 hover:bg-slate-600/80 transition disabled:opacity-40 disabled:pointer-events-none"
                  :disabled="!historyCanPrevPage"
                  @click="historyGoPrevPage"
                >
                  Anterior
                </button>
                <span class="text-[11px] text-slate-400">
                  Página {{ historyMeta.page }} / {{ historyMeta.total_pages }}
                </span>
                <button
                  type="button"
                  class="text-xs px-3 py-2 rounded-lg border border-slate-500 text-slate-200 hover:bg-slate-600/80 transition disabled:opacity-40 disabled:pointer-events-none"
                  :disabled="!historyCanNextPage"
                  @click="historyGoNextPage"
                >
                  Seguinte
                </button>
              </template>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal importação em lote (cadastros) -->
      <div
        v-if="bulkImportModalOpen"
        class="fixed inset-0 z-50 flex items-center justify-center px-4"
        @keydown.esc="closeBulkImportModal"
      >
        <div
          class="absolute inset-0 bg-slate-950/35 backdrop-blur-[2px]"
          :class="bulkImportBusy ? 'pointer-events-none' : 'cursor-pointer'"
          @click="!bulkImportBusy && closeBulkImportModal()"
        />
        <div
          class="relative w-full max-w-md max-h-[min(90vh,32rem)] rounded-2xl border border-slate-500 bg-slate-700 shadow-2xl shadow-black/35 flex flex-col overflow-hidden"
          role="dialog"
          aria-modal="true"
          aria-labelledby="bulk-import-modal-title"
        >
          <div class="px-5 py-4 border-b border-slate-500 flex items-center justify-between gap-3 shrink-0">
            <div>
              <h3 id="bulk-import-modal-title" class="text-sm font-semibold text-slate-100">
                Importar arquivos
              </h3>
              <p class="text-[11px] text-slate-300 mt-0.5 leading-snug">
                CSV ou Excel (.xlsx), até 5 arquivos por envio.
              </p>
            </div>
            <button
              type="button"
              class="shrink-0 text-xs px-3 py-2 rounded-lg border border-slate-400 text-slate-100 hover:bg-slate-600 transition disabled:opacity-50"
              :disabled="bulkImportBusy"
              @click="closeBulkImportModal()"
            >
              Fechar
            </button>
          </div>
          <div class="flex-1 min-h-0 overflow-y-auto p-5 space-y-3" :class="modalScrollbarClass">
            <label class="block text-[11px] text-slate-300">Agente de cadastro</label>
            <select
              v-model.number="bulkImportExecutorId"
              class="w-full text-xs px-3 py-2 rounded-lg border border-slate-500 bg-slate-800 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
            >
              <option
                v-for="opt in recordCreationExecutorsForLock"
                :key="`${opt.coordinator_id}-${opt.executor_id}`"
                :value="opt.executor_id"
                :title="recordCreationExecutorOptionTitle(opt)"
              >
                {{ recordCreationExecutorOptionLabel(opt) }}
              </option>
            </select>
            <input
              ref="bulkImportFileInput"
              type="file"
              accept=".csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
              multiple
              class="sr-only"
              :disabled="bulkImportBusy"
              @change="onBulkImportFiles"
            />
            <div class="flex flex-wrap gap-2 pt-1">
              <button
                type="button"
                class="text-xs px-3 py-2 rounded-lg border border-slate-400 text-slate-100 hover:bg-slate-600 transition disabled:opacity-50"
                :disabled="bulkImportBusy"
                @click="openBulkImportPicker"
              >
                Selecionar arquivos
              </button>
              <button
                type="button"
                class="text-xs px-3 py-2 rounded-lg bg-cyan-500 border border-cyan-300 text-slate-100 hover:bg-cyan-400 hover:text-slate-50 transition disabled:opacity-50 disabled:pointer-events-none"
                :disabled="bulkImportBusy || !bulkImportFiles.length"
                @click="submitRecordCreationImport"
              >
                {{ bulkImportBusy ? 'Enviando…' : 'Enviar arquivos' }}
              </button>
            </div>
            <p v-if="bulkImportFileNames.length" class="text-[10px] text-slate-300 break-words">
              {{ bulkImportFileNames.join(' · ') }}
            </p>
            <p v-if="bulkImportError" class="text-[11px] text-red-200 leading-snug bg-red-500/10 border border-red-400/25 rounded-lg px-3 py-2">
              {{ bulkImportError }}
            </p>
          </div>
        </div>
      </div>

      <div v-if="loadError" class="shrink-0 mb-3 p-4 bg-red-500/10 border border-red-400/40 text-red-300 rounded-xl text-sm">
        {{ loadError }}
      </div>

      <div
        class="flex flex-col lg:flex-row gap-4 lg:gap-5 lg:items-stretch flex-1 min-h-0"
        :class="loading ? 'min-h-[280px]' : ''"
      >
        <!-- Painel coordenadores -->
        <aside
          class="flex w-full shrink-0 flex-col overflow-hidden rounded-2xl border border-cyan-400/20 bg-slate-900/55 shadow-xl shadow-black/25 backdrop-blur-md sm:max-h-[min(17rem,34svh)] lg:max-h-full lg:min-h-0 lg:w-[300px]"
          :class="
            coordinatorsAsideMobileOpen
              ? 'max-sm:max-h-[min(22rem,80svh)]'
              : 'max-sm:max-h-[3.75rem]'
          "
        >
          <button
            type="button"
            class="flex w-full shrink-0 items-center gap-2 border-b border-cyan-400/15 bg-slate-950/40 px-3 py-2 text-left transition hover:bg-slate-900/80 sm:hidden"
            :aria-expanded="coordinatorsAsideMobileOpen ? 'true' : 'false'"
            aria-controls="coordinators-aside-panel"
            aria-label="Agentes ativos: expandir ou recolher"
            @click="coordinatorsAsideMobileOpen = !coordinatorsAsideMobileOpen"
          >
            <div class="min-w-0 flex-1">
              <h2 class="text-sm font-semibold text-slate-100">Agentes ativos</h2>
              <p
                v-if="lockedCoordinatorId"
                class="mt-0.5 truncate text-[10px] text-cyan-200/90"
                :title="coordinatorName(lockedCoordinatorId)"
              >
                Fixo: {{ coordinatorName(lockedCoordinatorId) }}
              </p>
            </div>
            <svg
              class="h-5 w-5 shrink-0 text-slate-400 transition-transform duration-200"
              :class="coordinatorsAsideMobileOpen ? 'rotate-180' : ''"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          <div class="hidden border-b border-cyan-400/15 bg-slate-950/40 px-3 py-2 sm:block sm:px-4 sm:py-3">
            <h2 class="text-sm font-semibold text-slate-100">Agentes ativos</h2>
            <p class="mt-0.5 hidden text-[11px] text-slate-400 sm:block">
              Pode escolher um agente ou deixar no automático
            </p>
          </div>

          <div
            id="coordinators-aside-panel"
            class="flex min-h-0 flex-1 flex-col overflow-hidden"
            :class="{ 'max-sm:hidden': !coordinatorsAsideMobileOpen }"
          >
          <div
            :class="[
              'min-h-0 flex-1 space-y-1.5 overflow-y-auto p-2 max-h-[15rem] sm:space-y-2 sm:p-3 lg:max-h-none',
              modalScrollbarClass
            ]"
          >
            <div v-if="loading" class="text-center py-8 text-slate-400 text-sm">Carregando…</div>
            <div
              v-else-if="!coordinators.length"
              class="text-sm text-slate-400 p-2 leading-relaxed"
            >
              Nenhum agente coordenador neste ambiente. Crie um em
              <span class="text-cyan-300">Agentes</span>.
            </div>
            <button
              v-for="c in coordinators"
              :key="c.id"
              type="button"
              class="group flex w-full items-center gap-2 rounded-xl border px-2.5 py-2 text-left transition-all duration-200 sm:gap-3 sm:px-3 sm:py-2.5"
              :class="coordinatorCardClass(c.id)"
              @click="selectCoordinatorForLock(c.id)"
            >
              <AgentAvatar
                class="shrink-0"
                :variant="c.avatar_variant"
                :color="c.avatar_color"
                :name="c.name"
                size="sm"
                :show-coordinator-star="false"
                ring-cyan
              />
              <div class="min-w-0 flex-1">
                <p class="text-sm font-medium text-slate-100 truncate">{{ c.name }}</p>
                <p
                  class="text-[10px] text-slate-400 line-clamp-2 break-words"
                  :title="coordinatorCardSubtitleTitle(c)"
                >
                  {{ coordinatorCardSubtitle(c) }}
                </p>
              </div>
              <span
                v-if="respondingCoordinatorId === c.id"
                class="shrink-0 w-2 h-2 rounded-full bg-cyan-400 shadow-[0_0_10px_#22d3ee] animate-pulse"
                aria-hidden="true"
              />
            </button>
          </div>

          <div
            v-if="routingMode === 'locked' && lockedCoordinatorId"
            class="border-t border-cyan-400/15 bg-slate-950/30 p-2 sm:p-3"
          >
            <div class="flex items-center gap-2 text-[11px] leading-snug text-cyan-200/90">
              <p class="min-w-0 flex-1">
                Mensagens sempre para
                <strong class="text-cyan-100">{{ coordinatorName(lockedCoordinatorId) }}</strong>.
              </p>
              <button
                v-if="recordCreationExecutorsForLock.length > 0"
                type="button"
                class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-cyan-400/45 bg-cyan-500/15 text-cyan-100 transition hover:border-cyan-300/55 hover:bg-cyan-500/25 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 disabled:pointer-events-none disabled:opacity-45"
                title="Importar arquivos (CSV ou Excel, até 5 por envio)"
                aria-label="Importar arquivos CSV ou Excel em lote"
                :disabled="bulkImportBusy"
                @click="openBulkImportModal"
              >
                <svg
                  class="h-[1.15rem] w-[1.15rem]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
              </button>
            </div>
          </div>
          </div>
        </aside>

        <!-- Área mensagens -->
        <section
          class="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border border-cyan-400/20 bg-slate-900/40 shadow-xl shadow-black/30 backdrop-blur-md"
        >
          <div
            ref="scrollArea"
            :class="['flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-4 scroll-smooth', modalScrollbarClass]"
          >
            <div
              v-if="!messages.length"
              class="flex h-full min-h-[min(200px,35svh)] flex-col items-center justify-center px-4 text-center sm:min-h-[200px] sm:px-6"
            >
              <div
                class="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500/25 to-slate-800/80 border border-cyan-400/25 flex items-center justify-center mb-4"
              >
                <svg class="w-8 h-8 text-cyan-300/90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <p class="text-slate-300 text-sm font-medium">Área de Interação do Chat</p>
              <p class="text-slate-400 text-xs mt-2 max-w-md">
                Ex.: Utilize o chat para fazer perguntas. A inteligência pode selecionar automaticamente o agente mais indicado para te responder — ou, se preferir, você pode escolher um agente específico.
              </p>
            </div>

            <article
              v-for="msg in messages"
              :key="msg.id"
              class="flex"
              :class="msg.role === 'user' ? 'justify-end' : 'justify-start'"
            >
              <div
                class="rounded-2xl px-4 py-3 shadow-md"
                :class="[
                  msg.role === 'user'
                    ? 'max-w-[min(100%,36rem)] bg-gradient-to-br from-cyan-400 to-cyan-500 text-slate-900 rounded-br-md'
                    : 'max-w-[min(100%,52rem)] bg-slate-800/90 border border-slate-600/60 text-slate-100 rounded-bl-md'
                ]"
              >
                <p
                  v-if="msg.role === 'user'"
                  class="text-sm whitespace-pre-wrap break-words leading-relaxed"
                >
                  {{ msg.body }}
                </p>
                <ChatAssistantBody
                  v-else
                  :content="msg.body"
                  :message-id="msg.id"
                />
                <div v-if="msg.role === 'assistant' && hasChatDetails(msg)" class="mt-2">
                  <button
                    type="button"
                    class="text-[11px] px-2 py-1 rounded border border-slate-500/70 text-slate-300 hover:bg-slate-700/70"
                    @click="toggleDetails(msg.id)"
                  >
                    {{ isDetailsOpen(msg.id) ? 'Ocultar fontes / parâmetros' : 'Ver fontes de dados e parâmetros' }}
                  </button>
                </div>
                <div v-if="msg.role === 'assistant' && hasChatDetails(msg) && isDetailsOpen(msg.id)" class="mt-2 space-y-3 text-xs">
                  <template v-if="chatDataSources(msg).length">
                    <div
                      v-for="(src, si) in chatDataSources(msg)"
                      :key="si"
                      class="rounded-lg border border-slate-600/60 p-2.5 bg-slate-900/55 space-y-2"
                    >
                      <p class="text-[11px] font-semibold text-cyan-200/95 leading-snug">
                        <span v-if="src.kind === 'api'">API</span>
                        <span v-else-if="src.kind === 'database_view'">View BD</span>
                        <span v-else>Fonte</span>
                        · {{ src.label || '—' }}
                        <span v-if="src.ok === false" class="text-red-300 font-normal ml-1">(falhou)</span>
                        <span v-else-if="src.ok === true" class="text-emerald-300/90 font-normal ml-1">(ok)</span>
                      </p>
                      <p v-if="src.endpoint_id != null" class="text-slate-400 text-[11px]">endpoint_id: {{ src.endpoint_id }}</p>
                      <p v-if="src.view_id != null" class="text-slate-400 text-[11px]">view_id: {{ src.view_id }}</p>
                      <p v-if="src.operation_key" class="text-slate-400 text-[11px] font-mono">operation_key: {{ src.operation_key }}</p>
                      <p v-if="src.request_url" class="text-slate-400 text-[11px] break-all">URL: {{ src.request_url }}</p>
                      <p v-if="src.http_status != null && src.http_status !== ''" class="text-slate-400 text-[11px]">HTTP {{ src.http_status }}</p>
                      <p v-if="src.executed != null" class="text-slate-400 text-[11px]">Executado no BD: {{ src.executed ? 'sim' : 'não' }}</p>
                      <div v-if="src.sql_query" class="space-y-0.5">
                        <p class="text-[10px] uppercase tracking-wide text-slate-400">SQL (preview)</p>
                        <pre class="p-2 rounded bg-slate-950/80 text-slate-300 overflow-x-auto max-h-32 whitespace-pre-wrap text-[11px]">{{ src.sql_query }}</pre>
                      </div>
                      <div v-if="src.parameters != null && objectKeysCount(src.parameters) > 0" class="space-y-0.5">
                        <p class="text-[10px] uppercase tracking-wide text-slate-400">Parâmetros enviados</p>
                        <pre class="p-2 rounded bg-slate-950/80 text-slate-300 overflow-x-auto max-h-44 whitespace-pre-wrap">{{ formatJsonDetails(src.parameters) }}</pre>
                      </div>
                      <p v-if="src.error" class="text-red-300/95 text-[11px]">{{ src.error }}</p>
                      <p v-if="src.message && src.ok === false" class="text-amber-200/90 text-[11px]">{{ src.message }}</p>
                    </div>
                  </template>
                  <template v-else>
                    <div v-if="msg.details.request_payload != null">
                      <p class="text-[10px] uppercase tracking-wide text-slate-300 mb-1">Parâmetros</p>
                      <pre class="p-2 rounded bg-slate-900/80 text-slate-300 overflow-x-auto max-h-56 whitespace-pre-wrap">{{ formatJsonDetails(msg.details.request_payload) }}</pre>
                    </div>
                  </template>
                </div>
                <footer v-if="msg.role === 'assistant' && msg.coordinatorName" class="mt-2 pt-2 border-t border-slate-600/50">
                  <p class="text-[11px] text-cyan-300/95 font-medium">
                    Resposta: <span class="text-cyan-200">{{ msg.coordinatorName }}</span>
                    <span v-if="msg.executorName" class="text-cyan-200/90"> (Agente {{ msg.executorName }})</span>
                  </p>
                </footer>
              </div>
            </article>

            <div
              v-if="sending"
              class="flex justify-start"
            >
              <div class="bg-slate-800/60 border border-slate-600/40 rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-3">
                <span
                  class="inline-flex gap-1"
                  aria-hidden="true"
                >
                  <span class="w-1.5 h-1.5 rounded-full bg-cyan-400/80 animate-bounce" style="animation-delay: 0ms" />
                  <span class="w-1.5 h-1.5 rounded-full bg-cyan-400/80 animate-bounce" style="animation-delay: 150ms" />
                  <span class="w-1.5 h-1.5 rounded-full bg-cyan-400/80 animate-bounce" style="animation-delay: 300ms" />
                </span>
                <span class="text-xs text-slate-400">
                  {{ chatSendingLabel }}
                </span>
              </div>
            </div>
          </div>

          <div class="shrink-0 border-t border-cyan-400/15 bg-slate-950/35 p-2 sm:p-4">
            <form class="flex items-end gap-2" @submit.prevent="sendMessage">
              <label class="sr-only" for="chat-input">Mensagem</label>
              <textarea
                id="chat-input"
                v-model="draft"
                rows="2"
                placeholder="Escreva sua mensagem…"
                class="max-h-24 min-h-[2.5rem] flex-1 resize-y overflow-y-auto overflow-x-hidden rounded-xl border border-slate-500/70 bg-slate-900/70 px-3 py-1.5 text-sm leading-relaxed text-slate-100 placeholder:text-slate-400 focus:border-cyan-400/50 focus:outline-none focus:ring-2 focus:ring-cyan-400/35 sm:max-h-28 sm:min-h-[2.875rem] sm:px-4 sm:py-2"
                :class="modalScrollbarClass"
                :disabled="sending"
                @keydown.enter.exact.prevent="sendMessage"
              />
              <span
                v-if="speechSupported"
                class="relative shrink-0 h-11 w-11 inline-flex items-center justify-center"
              >
                <span
                  v-show="speechListening"
                  class="pointer-events-none absolute inset-0 flex items-center justify-center mic-wave-host"
                  aria-hidden="true"
                >
                  <span class="mic-wave-ring" />
                  <span class="mic-wave-ring mic-wave-ring-delay-1" />
                  <span class="mic-wave-ring mic-wave-ring-delay-2" />
                </span>
                <button
                  type="button"
                  class="relative z-10 h-11 w-11 inline-flex items-center justify-center rounded-xl border transition disabled:opacity-45 disabled:pointer-events-none overflow-visible"
                  :class="
                    speechListening
                      ? 'border-red-400/55 bg-red-500/15 text-red-200 shadow-[0_0_12px_rgba(248,113,113,0.25)]'
                      : 'border-slate-500/70 bg-slate-800/80 text-slate-300 hover:border-cyan-400/40 hover:text-cyan-200 hover:bg-slate-800'
                  "
                  :aria-pressed="speechListening"
                  :aria-label="speechListening ? 'Parar microfone' : 'Ditar por voz'"
                  :title="speechListening ? 'Parar gravação' : 'Falar para transcrever (pt-BR)'"
                  :disabled="sending"
                  @click="toggleVoiceInput"
                >
                  <!-- Barras tipo onda à esquerda do ícone (visíveis por cima do fundo) -->
                  <svg
                    v-if="speechListening"
                    class="absolute left-1 top-1/2 -translate-y-1/2 w-2.5 h-5 text-amber-200/90 pointer-events-none overflow-visible"
                    viewBox="0 0 10 20"
                    fill="none"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      class="mic-bar mic-bar--1"
                      stroke-linecap="round"
                      stroke-width="2"
                      d="M2 14V6"
                    />
                    <path
                      class="mic-bar mic-bar--2"
                      stroke-linecap="round"
                      stroke-width="2"
                      d="M5 16V4"
                    />
                    <path
                      class="mic-bar mic-bar--3"
                      stroke-linecap="round"
                      stroke-width="2"
                      d="M8 13V7"
                    />
                  </svg>
                  <svg
                    class="relative z-10 w-5 h-5"
                    :class="{ 'ml-0.5': speechListening }"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                    />
                  </svg>
                </button>
              </span>
              <button
                type="submit"
                class="shrink-0 h-11 px-5 rounded-xl bg-gradient-to-r from-cyan-400 to-cyan-500 hover:from-cyan-300 hover:to-cyan-400 text-slate-900 text-sm font-semibold shadow-lg shadow-cyan-500/35 disabled:opacity-50 disabled:pointer-events-none transition"
                :disabled="sending || !draftTrimmed"
              >
                Enviar
              </button>
            </form>
            <p v-if="speechError" class="text-[10px] text-amber-300/90 mt-2 px-1">{{ speechError }}</p>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { useApi } from '../composables/useApi'
import { waitForTerminalAgentRun } from '../composables/useAgentRunCable'
import AgentAvatar from './AgentAvatar.vue'
import ChatAssistantBody from './ChatAssistantBody.vue'
import {
  DEFAULT_AGENT_AVATAR_VARIANT,
  DEFAULT_AGENT_AVATAR_COLOR
} from '../constants/agentAvatarPresets.js'

function randomId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

export default {
  name: 'CoordinatorChat',

  components: { AgentAvatar, ChatAssistantBody },

  setup() {
    const { get, post, postFormData } = useApi()
    return { get, post, postFormData }
  },

  data() {
    return {
      /** Barra de scroll igual ao modal criar/editar agente (Agents.vue). */
      modalScrollbarClass:
        '[scrollbar-width:thin] [scrollbar-color:rgba(103,232,249,0.45)_rgba(47,69,99,0.4)] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-[#2f4563]/40 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-cyan-300/45 [&::-webkit-scrollbar-thumb]:border [&::-webkit-scrollbar-thumb]:border-[#2f4563]/80 [&::-webkit-scrollbar-thumb:hover]:bg-cyan-300/70',
      coordinators: [],
      loading: true,
      loadError: null,
      conversationId: '',
      messages: [],
      draft: '',
      sending: false,
      respondingCoordinatorId: null,
      routingMode: 'auto',
      lockedCoordinatorId: null,
      /** Abaixo do breakpoint sm: painel «Agentes ativos» começa fechado; toque no cabeçalho expande/recolhe. */
      coordinatorsAsideMobileOpen: false,
      historyOpen: false,
      historyLoading: false,
      historyError: null,
      historyItems: [],
      historyPerPage: 20,
      historyPage: 1,
      historySessionQuery: '',
      historyDateQuery: '',
      historyMeta: {
        page: 1,
        per_page: 20,
        total_count: null,
        total_pages: 0
      },
      historyLoadingConversationId: null,
      detailsOpenById: {},
      recordCreationImportLinks: [],
      bulkImportExecutorId: null,
      bulkImportFiles: [],
      bulkImportBusy: false,
      bulkImportError: '',
      bulkImportModalOpen: false,
      speechListening: false,
      speechError: '',
      /** @type {SpeechRecognition | null} */
      _speechRec: null
    }
  },

  computed: {
    draftTrimmed() {
      return (this.draft || '').trim()
    },
    conversationShort() {
      const id = this.conversationId || ''
      if (id.length <= 12) return id
      return `${id.slice(0, 8)}…`
    },
    chatSendingLabel() {
      if (this.respondingCoordinatorId) {
        return `${this.coordinatorName(this.respondingCoordinatorId)} esta respondendo…`
      }
      return 'Pesquisando...'
    },
    speechSupported() {
      return !!this.getSpeechRecognitionCtor()
    },
    historyHasActiveFilters() {
      return !!(this.historySessionQuery || this.historyDateQuery)
    },
    historyCanPrevPage() {
      return this.historyMeta.page > 1
    },
    historyCanNextPage() {
      return this.historyMeta.page < this.historyMeta.total_pages
    },
    recordCreationExecutorsForLock() {
      const cid = this.lockedCoordinatorId
      if (cid == null) return []
      return this.recordCreationImportLinks.filter((l) => Number(l.coordinator_id) === Number(cid))
    },
    bulkImportFileNames() {
      return this.bulkImportFiles.map((f) => f.name)
    }
  },

  watch: {
    recordCreationExecutorsForLock: {
      handler(list) {
        if (!list.length) {
          this.bulkImportExecutorId = null
          if (this.bulkImportModalOpen) this.closeBulkImportModal(true)
          return
        }
        const current = Number(this.bulkImportExecutorId)
        const ok = list.some((x) => Number(x.executor_id) === current)
        if (!ok) this.bulkImportExecutorId = Number(list[0].executor_id)
      },
      deep: true
    }
  },

  mounted() {
    this.conversationId = randomId()
    this.loadCoordinators()
  },

  beforeUnmount() {
    this.stopVoiceInput(true)
  },

  methods: {
    getSpeechRecognitionCtor() {
      if (typeof window === 'undefined') return null
      return window.SpeechRecognition || window.webkitSpeechRecognition || null
    },

    toggleVoiceInput() {
      if (this.speechListening) {
        this.stopVoiceInput(false)
        return
      }
      this.startVoiceInput()
    },

    startVoiceInput() {
      const Ctor = this.getSpeechRecognitionCtor()
      if (!Ctor || this.sending) return
      this.speechError = ''
      try {
        if (this._speechRec) {
          try {
            this._speechRec.abort()
          } catch {
            /* ignore */
          }
          this._speechRec = null
        }
        const rec = new Ctor()
        rec.lang = 'pt-BR'
        /** Em vários telemóveis (Chrome/Android) `continuous` + `interimResults` dispara o mesmo final várias vezes. */
        rec.interimResults = false
        const narrow =
          typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches
        const coarse =
          typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches
        rec.continuous = !(narrow || coarse)
        try {
          rec.maxAlternatives = 1
        } catch {
          /* ignore */
        }
        let lastFinalNorm = ''
        let lastFinalAt = 0
        rec.onstart = () => {
          this.speechListening = true
        }
        rec.onend = () => {
          this.speechListening = false
          this._speechRec = null
        }
        rec.onerror = (ev) => {
          this.speechListening = false
          const code = ev.error
          if (code === 'not-allowed') {
            this.speechError = 'Microfone bloqueado: permita o acesso nas definições do navegador.'
          } else if (code === 'no-speech') {
            this.speechError = 'Não foi ouvida voz. Tente de novo.'
          } else if (code !== 'aborted') {
            this.speechError = 'Erro no reconhecimento de voz. Tente outro navegador (Chrome/Edge).'
          }
        }
        rec.onresult = (e) => {
          for (let i = e.resultIndex; i < e.results.length; i += 1) {
            if (!e.results[i].isFinal) continue
            const t = (e.results[i][0].transcript || '').replace(/\s+/g, ' ').trim()
            if (!t) continue
            const norm = t.toLowerCase()
            const now = Date.now()
            if (norm === lastFinalNorm && now - lastFinalAt < 500) continue
            lastFinalNorm = norm
            lastFinalAt = now
            const d = this.draft || ''
            const sep = d.length > 0 && !/\s$/.test(d) ? ' ' : ''
            this.draft = (d + sep + t).trim()
          }
        }
        this._speechRec = rec
        rec.start()
      } catch {
        this.speechError = 'Não foi possível usar o microfone neste dispositivo.'
      }
    },

    stopVoiceInput(abort = false) {
      if (!this._speechRec) {
        this.speechListening = false
        return
      }
      try {
        if (abort) {
          this._speechRec.abort()
        } else {
          this._speechRec.stop()
        }
      } catch {
        /* ignore */
      }
      this._speechRec = null
      this.speechListening = false
    },

    formatDateTime(raw) {
      try {
        const d = new Date(raw)
        if (Number.isNaN(d.getTime())) return ''
        return d.toLocaleString()
      } catch {
        return ''
      }
    },

    async openHistory() {
      this.historyOpen = true
      this.historySessionQuery = ''
      this.historyDateQuery = ''
      this.historyPage = 1
      this.historyMeta = { page: 1, per_page: this.historyPerPage, total_count: null, total_pages: 0 }
      await this.fetchHistoryConversations()
    },

    buildChatConversationsQuery() {
      const p = new URLSearchParams()
      p.set('page', String(this.historyPage))
      p.set('per_page', String(this.historyPerPage))
      const sid = (this.historySessionQuery || '').trim()
      if (sid) p.set('conversation_id', sid)
      const d = (this.historyDateQuery || '').trim()
      if (d) p.set('date', d)
      return p.toString()
    },

    async fetchHistoryConversations() {
      this.historyLoading = true
      this.historyError = null
      this.historyItems = []
      const qs = this.buildChatConversationsQuery()
      const { data, error } = await this.get(`/api/v1/agents/chat_conversations?${qs}`)
      this.historyLoading = false
      if (error || !data) {
        this.historyError = 'Não foi possível carregar o histórico.'
        this.historyMeta = { page: 1, per_page: this.historyPerPage, total_count: 0, total_pages: 0 }
        return
      }
      this.historyItems = Array.isArray(data.data) ? data.data : []
      const m = data.meta && typeof data.meta === 'object' ? data.meta : {}
      this.historyPage = Number(m.page) || this.historyPage
      this.historyMeta = {
        page: Number(m.page) || 1,
        per_page: Number(m.per_page) || this.historyPerPage,
        total_count: m.total_count != null ? Number(m.total_count) : this.historyItems.length,
        total_pages: Number(m.total_pages) || 0
      }
    },

    applyHistoryFilters() {
      this.historyPage = 1
      this.fetchHistoryConversations()
    },

    clearHistoryFilters() {
      this.historySessionQuery = ''
      this.historyDateQuery = ''
      this.historyPage = 1
      this.fetchHistoryConversations()
    },

    historyGoPrevPage() {
      if (!this.historyCanPrevPage) return
      this.historyPage -= 1
      this.fetchHistoryConversations()
    },

    historyGoNextPage() {
      if (!this.historyCanNextPage) return
      this.historyPage += 1
      this.fetchHistoryConversations()
    },

    closeHistory(force = false) {
      if (this.historyLoadingConversationId && !force) return
      this.historyOpen = false
    },

    async loadConversation(conversationId) {
      const cid = (conversationId || '').trim()
      if (!cid) return
      this.historyLoadingConversationId = cid
      try {
        const { data, error } = await this.get(
          `/api/v1/agents/chat_conversation?conversation_id=${encodeURIComponent(cid)}`
        )
        if (error || !data || !data.data) {
          this.historyError = 'Não foi possível carregar esta conversa.'
          return
        }
        const turns = (data.data.turns && Array.isArray(data.data.turns)) ? data.data.turns : []
        this.conversationId = cid
        this.messages = turns.map((t) => {
          const role = t.role === 'assistant' ? 'assistant' : 'user'
          return { id: randomId(), role, body: t.content }
        })
        this.closeHistory(true)
        this.scrollToBottom()
      } finally {
        this.historyLoadingConversationId = null
      }
    },

    async loadCoordinators() {
      this.loading = true
      this.loadError = null
      const { data, error } = await this.get('/api/v1/agents/connector_options')
      this.loading = false
      if (error || !data) {
        this.loadError = 'Não foi possível carregar os agentes.'
        return
      }
      this.recordCreationImportLinks = Array.isArray(data.record_creation_import_links)
        ? data.record_creation_import_links
        : []

      const raw = data.coordinator_options || []
      this.coordinators = raw.map((o) => ({
        id: o.id,
        name: o.name || o.label || `Agente ${o.id}`,
        description: (o.description && String(o.description).trim()) || '',
        avatar_variant: o.avatar_variant || DEFAULT_AGENT_AVATAR_VARIANT,
        avatar_color: o.avatar_color || DEFAULT_AGENT_AVATAR_COLOR
      }))
      if (
        this.lockedCoordinatorId &&
        !this.coordinators.some((c) => Number(c.id) === Number(this.lockedCoordinatorId))
      ) {
        this.clearLock()
      }
    },

    coordinatorName(id) {
      if (id == null) return 'Agente'
      const c = this.coordinators.find((x) => Number(x.id) === Number(id))
      return c ? c.name : 'Agente'
    },

    coordinatorCardSubtitle(c) {
      const raw = (c && c.description) ? String(c.description).trim() : ''
      if (raw.length > 0) {
        return raw
      }
      return this.lockedCoordinatorId === c.id
        ? 'Agente selecionado para responder'
        : 'Clique para falar só com este agente'
    },

    coordinatorCardSubtitleTitle(c) {
      const full = (c && c.description) ? String(c.description).trim() : ''
      const hint =
        this.lockedCoordinatorId === c.id
          ? 'Clique para desmarcar este agente.'
          : 'Clique para selecionar este agente.'
      if (full) {
        return `${full}\n\n${hint}`
      }
      return hint
    },

    coordinatorCardClass(id) {
      const isLocked = this.lockedCoordinatorId === id
      const isResponding = this.respondingCoordinatorId === id
      let base = 'border-slate-600/50 bg-slate-950/25 hover:border-cyan-400/35 hover:bg-slate-800/40'
      if (isLocked) {
        base = 'border-cyan-400/45 bg-cyan-500/10 hover:bg-cyan-500/15'
      }
      if (isResponding) {
        base += ' ring-2 ring-cyan-400/70 shadow-[0_0_18px_rgba(34,211,238,0.25)] animate-pulse'
      }
      return base
    },

    selectCoordinatorForLock(id) {
      if (this.lockedCoordinatorId != null && Number(this.lockedCoordinatorId) === Number(id)) {
        this.clearLock()
        return
      }
      this.lockedCoordinatorId = id
      this.routingMode = 'locked'
      this.coordinatorsAsideMobileOpen = true
    },

    clearLock() {
      this.closeBulkImportModal(true)
      this.lockedCoordinatorId = null
      this.routingMode = 'auto'
      this.bulkImportFiles = []
      this.bulkImportError = ''
      if (this.$refs.bulkImportFileInput) this.$refs.bulkImportFileInput.value = ''
    },

    openBulkImportModal() {
      this.bulkImportError = ''
      this.bulkImportModalOpen = true
    },

    closeBulkImportModal(force = false) {
      if (this.bulkImportBusy && !force) return
      this.bulkImportModalOpen = false
      this.bulkImportError = ''
      this.bulkImportFiles = []
      if (this.$refs.bulkImportFileInput) this.$refs.bulkImportFileInput.value = ''
    },

    recordCreationExecutorOptionLabel(opt) {
      const name = (opt && opt.executor_name) ? String(opt.executor_name).trim() : 'Agente'
      const raw = (opt && opt.executor_description) ? String(opt.executor_description).trim() : ''
      if (!raw) return name
      const max = 56
      const short = raw.length > max ? `${raw.slice(0, max)}…` : raw
      return `${name} (${short})`
    },

    recordCreationExecutorOptionTitle(opt) {
      const name = (opt && opt.executor_name) ? String(opt.executor_name).trim() : ''
      const raw = (opt && opt.executor_description) ? String(opt.executor_description).trim() : ''
      if (!raw) return name || ''
      return name ? `${name} — ${raw}` : raw
    },

    openBulkImportPicker() {
      const el = this.$refs.bulkImportFileInput
      if (el) el.click()
    },

    onBulkImportFiles(event) {
      this.bulkImportError = ''
      const fl = Array.from((event.target && event.target.files) || [])
      if (fl.length > 5) {
        this.bulkImportError = 'No máximo 5 ficheiros por envio.'
        this.bulkImportFiles = []
        event.target.value = ''
        return
      }
      this.bulkImportFiles = fl
    },

    async submitRecordCreationImport() {
      this.bulkImportError = ''
      if (this.routingMode !== 'locked' || !this.lockedCoordinatorId) {
        this.bulkImportError = 'Ative o encaminhamento fixo e selecione um coordenador.'
        return
      }
      if (!this.bulkImportExecutorId) {
        this.bulkImportError = 'Selecione o agente de cadastro.'
        return
      }
      if (!this.bulkImportFiles.length) {
        this.bulkImportError = 'Escolha pelo menos um ficheiro.'
        return
      }
      if (this.bulkImportFiles.length > 5) {
        this.bulkImportError = 'No máximo 5 ficheiros por envio.'
        return
      }

      this.bulkImportBusy = true
      try {
        const fd = new FormData()
        fd.append('coordinator_id', String(this.lockedCoordinatorId))
        fd.append('executor_agent_id', String(this.bulkImportExecutorId))
        fd.append('conversation_id', this.conversationId)
        this.bulkImportFiles.forEach((f) => fd.append('files[]', f))

        const { data, error } = await this.postFormData('/api/v1/agents/record_creation_imports', fd)
        if (error) {
          const msg =
            (error.response &&
              error.response.data &&
              Array.isArray(error.response.data.errors) &&
              error.response.data.errors.join(' ')) ||
            'Não foi possível enviar a importação.'
          this.bulkImportError = msg
          return
        }
        const msg =
          (data && data.data && data.data.user_message) ||
          'Ficheiros enviados. Acompanhe as execuções no registo do agente.'
        this.messages.push({
          id: randomId(),
          role: 'user',
          body: msg
        })
        this.bulkImportFiles = []
        if (this.$refs.bulkImportFileInput) this.$refs.bulkImportFileInput.value = ''
        this.closeBulkImportModal(true)
        this.scrollToBottom()
      } catch (e) {
        this.bulkImportError = (e && e.message) || 'Erro inesperado.'
      } finally {
        this.bulkImportBusy = false
      }
    },

    startNewConversation() {
      this.conversationId = randomId()
      this.messages = []
      this.draft = ''
      this.respondingCoordinatorId = null
      this.sending = false
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.scrollArea
        if (el) el.scrollTop = el.scrollHeight
      })
    },

    pushAssistantFromRunResult(finalRun, coordinatorId, executorNameHint, unexpectedBody) {
      const responderId =
        coordinatorId != null && Number.isFinite(coordinatorId)
          ? coordinatorId
          : this.respondingCoordinatorId
      const name = this.coordinatorName(responderId)
      if (unexpectedBody) {
        this.messages.push({
          id: randomId(),
          role: 'assistant',
          body: unexpectedBody,
          coordinatorName: name,
          executorName: executorNameHint,
          coordinatorId: responderId
        })
        return
      }
      if (!finalRun) {
        this.messages.push({
          id: randomId(),
          role: 'assistant',
          body:
            'A resposta demorou demais (fila ou rede). Confira a execução no painel de agentes ou tente de novo.',
          coordinatorName: name,
          executorName: executorNameHint,
          coordinatorId: responderId
        })
        return
      }
      const executorName = (finalRun && finalRun.agent_name) || executorNameHint
      if (finalRun.status === 'succeeded') {
        const body =
          (finalRun.output_payload && finalRun.output_payload.text) || '(Sem texto de resposta)'
        const details = finalRun.output_payload && finalRun.output_payload.details
        this.messages.push({
          id: randomId(),
          role: 'assistant',
          body,
          details: details || null,
          coordinatorName: name,
          executorName,
          coordinatorId: responderId
        })
      } else if (finalRun.status === 'failed') {
        const em = finalRun.error_message || 'Execução falhou.'
        this.messages.push({
          id: randomId(),
          role: 'assistant',
          body: `Erro: ${em}`,
          coordinatorName: name,
          executorName,
          coordinatorId: responderId
        })
      } else {
        this.messages.push({
          id: randomId(),
          role: 'assistant',
          body: `Estado final: ${finalRun.status}.`,
          coordinatorName: name,
          executorName,
          coordinatorId: responderId
        })
      }
    },

    async sendMessage() {
      const text = this.draftTrimmed
      if (!text || this.sending) return

      this.messages.push({
        id: randomId(),
        role: 'user',
        body: text
      })
      this.draft = ''
      this.scrollToBottom()

      if (!this.coordinators.length) {
        this.messages.push({
          id: randomId(),
          role: 'assistant',
          body: 'Não há agentes disponíveis para encaminhar a mensagem.',
          coordinatorName: null
        })
        this.scrollToBottom()
        return
      }

      this.sending = true
      if (this.routingMode === 'locked' && this.lockedCoordinatorId) {
        this.respondingCoordinatorId = this.lockedCoordinatorId
      } else {
        this.respondingCoordinatorId = null
      }
      this.scrollToBottom()

      try {
        const payload = {
          message: text,
          conversation_id: this.conversationId
        }
        if (this.routingMode === 'locked' && this.lockedCoordinatorId) {
          payload.coordinator_id = this.lockedCoordinatorId
        }
        const { data, error } = await this.post('/api/v1/agents/chat', payload)
        if (error) {
          const msg =
            (error.response && error.response.data && Array.isArray(error.response.data.errors) &&
              error.response.data.errors.join(' ')) ||
            'Não foi possível enviar a mensagem.'
          this.messages.push({ id: randomId(), role: 'assistant', body: msg, coordinatorName: null })
          return
        }
        const d = data && data.data
        const sequentialRuns = d && d.sequential && Array.isArray(d.runs) ? d.runs : null
        if (sequentialRuns && sequentialRuns.length > 0) {
          for (const entry of sequentialRuns) {
            const rid = entry && entry.agent_run_id
            const coordFromServer =
              entry && entry.coordinator_id != null ? Number(entry.coordinator_id) : null
            const executorFromServer =
              entry && entry.executor_agent_name ? String(entry.executor_agent_name) : null
            if (coordFromServer != null && Number.isFinite(coordFromServer)) {
              this.respondingCoordinatorId = coordFromServer
            }
            if (!rid) {
              this.pushAssistantFromRunResult(
                null,
                coordFromServer,
                executorFromServer,
                'Resposta inesperada do servidor.'
              )
              continue
            }
            const finalRun = await waitForTerminalAgentRun(rid, { get: this.get })
            this.pushAssistantFromRunResult(finalRun, coordFromServer, executorFromServer, null)
            this.scrollToBottom()
          }
          return
        }

        const runId = d && d.agent_run_id
        const coordFromServer =
          d && d.coordinator_id != null ? Number(d.coordinator_id) : null
        const executorFromServer =
          d && d.executor_agent_name ? String(d.executor_agent_name) : null
        if (coordFromServer != null && Number.isFinite(coordFromServer)) {
          this.respondingCoordinatorId = coordFromServer
        }
        if (!runId) {
          this.messages.push({
            id: randomId(),
            role: 'assistant',
            body: 'Resposta inesperada do servidor.',
            coordinatorName: null
          })
          return
        }
        const finalRun = await waitForTerminalAgentRun(runId, { get: this.get })
        this.pushAssistantFromRunResult(finalRun, coordFromServer, executorFromServer, null)
      } catch (e) {
        this.messages.push({
          id: randomId(),
          role: 'assistant',
          body: (e && e.message) || 'Erro inesperado.',
          coordinatorName: null
        })
      } finally {
        this.respondingCoordinatorId = null
        this.sending = false
        this.scrollToBottom()
      }
    },
    toggleDetails(messageId) {
      const key = String(messageId)
      this.detailsOpenById = {
        ...this.detailsOpenById,
        [key]: !this.detailsOpenById[key]
      }
    },
    isDetailsOpen(messageId) {
      return !!this.detailsOpenById[String(messageId)]
    },
    formatJsonDetails(payload) {
      if (typeof payload === 'string') return payload
      try {
        return JSON.stringify(payload, null, 2)
      } catch {
        return String(payload)
      }
    },
    hasChatDetails(msg) {
      const d = msg && msg.details
      if (!d || typeof d !== 'object') return false
      if (Array.isArray(d.data_sources) && d.data_sources.length > 0) return true
      return d.request_payload != null
    },
    chatDataSources(msg) {
      const d = msg && msg.details
      if (!d || !Array.isArray(d.data_sources)) return []
      return d.data_sources
    },
    objectKeysCount(obj) {
      if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return 0
      return Object.keys(obj).length
    }
  }
}
</script>

<style scoped>
/* Ondas ao redor do microfone enquanto escuta (Web Speech API não expõe nível de áudio — animação decorativa) */
.mic-wave-host {
  z-index: 0;
}

.mic-wave-ring {
  position: absolute;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 9999px;
  border: 2px solid rgba(248, 113, 113, 0.5);
  animation: coordinator-chat-mic-wave 1.75s ease-out infinite;
}

.mic-wave-ring-delay-1 {
  animation-delay: 0.45s;
}

.mic-wave-ring-delay-2 {
  animation-delay: 0.9s;
}

@keyframes coordinator-chat-mic-wave {
  0% {
    transform: scale(0.92);
    opacity: 0.7;
  }
  100% {
    transform: scale(2.15);
    opacity: 0;
  }
}

/* Barras verticais animadas (efeito “som”) — Web Speech não expõe volume; é decorativo */
.mic-bar {
  transform-origin: center;
  animation: coordinator-chat-mic-bar 0.42s ease-in-out infinite alternate;
}

.mic-bar--1 {
  animation-delay: 0ms;
}

.mic-bar--2 {
  animation-delay: 0.1s;
}

.mic-bar--3 {
  animation-delay: 0.2s;
}

@keyframes coordinator-chat-mic-bar {
  0% {
    transform: scaleY(0.35);
    opacity: 0.45;
  }
  100% {
    transform: scaleY(1);
    opacity: 1;
  }
}

/* Ícone do calendário nativo: filter/invert falha em vários Chromium; usamos SVG claro no indicador. */
.history-modal-date {
  color-scheme: dark;
}

.history-modal-date::-webkit-calendar-picker-indicator {
  cursor: pointer;
  width: 1.125rem;
  height: 1.125rem;
  padding: 0.125rem;
  margin-left: 0.25rem;
  opacity: 1;
  background: center / contain no-repeat
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23e2e8f0' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z'/%3E%3C/svg%3E");
}
</style>
